<template>
  <main class="safe-page">
    <div class="mobile-shell article-page">
      <header class="article-page__header">
        <app-image class="article-page__logo" src="@/assets/images/icon_logo.png" alt="" />
        <div class="article-page__header-text">
          <div data-strong>WinPay</div>
          <span>H5</span>
        </div>
      </header>

      <van-pull-refresh v-model="refreshing" @refresh="handleRefresh">
        <section v-if="visible" class="article-card" @click="goDetail">
          <div class="article-card__top">
            <div class="article-card__author">
              <div class="article-card__avatar">
                <app-image v-if="avatarUrl" class="article-card__avatar-image" :src="avatarUrl" alt="" />
                <span v-else>{{ avatarText }}</span>
              </div>
              <div class="article-card__meta">
                <div data-strong>{{ t('article.sampleName') }}</div>
                <span>{{ t('article.now') }}</span>
              </div>
            </div>

            <div class="article-card__actions">
              <van-button plain round size="small" type="primary" class="article-card__follow">
                <van-icon name="plus" />
                <span>{{ t('article.follow') }}</span>
              </van-button>
              <button class="article-card__report" type="button" @click.stop="showReport = true">
                <van-icon name="cross" />
              </button>
            </div>
          </div>

          <div class="article-card__body">
            <h1>{{ t('article.sampleTopic') }}</h1>
            <p>{{ t('article.sampleContent') }}</p>
          </div>

          <div class="article-card__footer">
            <span>
              <app-image src="@/assets/images/img_views.png" alt="" />
              {{ formatNumber(1288) }}
            </span>
            <span>
              <app-image src="@/assets/images/img_like.png" alt="" />
              {{ formatNumber(86) }}
            </span>
          </div>
        </section>

        <van-empty v-else image="success" :description="t('article.reportSuccess')" />
      </van-pull-refresh>
    </div>

    <ArticleReportSheet
      v-model="showReport"
      :article-id="sampleArticle.id"
      :submitter="mockReport"
      @success="handleReportSuccess"
      @fail="handleReportFail"
    />
  </main>
</template>

<script setup>
import { computed, ref } from 'vue';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import ArticleReportSheet from '@/view/article/components/ArticleReportSheet.vue';
import { useProfileStore } from '@/stores/profile';
import { resolveAvatarText, resolveAvatarUrl } from '@/utils/avatar';
import { formatNumber, shortAddress } from '@/utils/format';

const { t } = useI18n();
const profileStore = useProfileStore();
const showReport = ref(false);
const refreshing = ref(false);
const visible = ref(true);

const sampleArticle = {
  id: 10001,
  address: '0xf4b8e5a40bb13f3fb9cba93c71e2c80cf7937781'
};

const avatarUrl = computed(() => resolveAvatarUrl(profileStore.currentAccount?.avatar));
const avatarText = computed(() => {
  const accountName = profileStore.currentAccount?.name;
  if (accountName) {
    return resolveAvatarText(accountName);
  }
  const address = profileStore.currentAddress || sampleArticle.address;
  return resolveAvatarText(shortAddress(address), 'WP');
});

function handleRefresh() {
  setTimeout(() => {
    visible.value = true;
    refreshing.value = false;
    showToast('OK');
  }, 350);
}

function goDetail() {
  showToast('article_details');
}

function mockReport() {
  return new Promise((resolve) => {
    setTimeout(resolve, 450);
  });
}

function handleReportSuccess() {
  visible.value = false;
  showPassedToast(t('article.reportSuccess'));
}

function handleReportFail(error) {
  showFailToast(error?.message || t('article.reportFailed'));
}
</script>

<style scoped>
.article-page {
  padding-bottom: 24px;
}

.article-page__header {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 var(--wp-page-padding);
  background: var(--wp-color-white);
}

.article-page__logo {
  width: 28px;
  height: 28px;
}

.article-page__header-text {
  display: flex;
  flex-direction: column;
  color: var(--wp-color-text);
  line-height: 1.1;
}

.article-page__header-text [data-strong] {
  font-size: 16px;
  font-weight: 500;
}

.article-page__header-text span {
  margin-top: 2px;
  color: var(--wp-color-sub-text);
  font-size: 11px;
}

.article-card {
  margin-top: 8px;
  padding: var(--wp-page-padding);
  background: var(--wp-color-white);
}

.article-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.article-card__author {
  display: flex;
  min-width: 0;
  align-items: center;
}

.article-card__avatar {
  display: flex;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2667ff, #14c9c9);
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
}

.article-card__avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.article-card__meta {
  min-width: 0;
  margin-left: 10px;
}

.article-card__meta [data-strong],
.article-card__meta span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.article-card__meta [data-strong] {
  max-width: 140px;
  color: #000000;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
}

.article-card__meta span {
  margin-top: 2px;
  color: var(--wp-color-sub-text);
  font-size: 12px;
  line-height: 17px;
}

.article-card__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 10px;
}

.article-card__follow {
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
}

.article-card__follow :deep(.van-button__content) {
  gap: 2px;
}

.article-card__report {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--wp-color-border);
  font-size: 20px;
}

.article-card__body {
  padding-left: 50px;
}

.article-card__body h1 {
  margin: 16px 0 0;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}

.article-card__body p {
  display: -webkit-box;
  overflow: hidden;
  margin: 8px 0 0;
  color: var(--wp-color-sub-text);
  font-size: 14px;
  line-height: 21px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.article-card__footer {
  display: flex;
  gap: 32px;
  padding: 16px 0 0 50px;
  color: var(--wp-color-sub-text);
  font-size: 14px;
}

.article-card__footer span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.article-card__footer .app-image {
  width: 16px;
  height: 16px;
  opacity: 0.65;
}
</style>
